/* ============================================================
   ToastProvider.jsx — Context Provider de la stack de toasts
   ============================================================
   Sprint 2 P2 — design system global.

   Architecture :
   - State : array de toasts { id, variant, message, duration }
   - Stack FIFO max 3 : si un 4e arrive, le plus ancien est dismiss
   - Position container :
       desktop → bottom-right
       mobile (Capacitor native ou viewport <=768px) → top-center
         avec safe-area-inset-top pour passer sous le notch iOS
   - Auto-dismiss : setTimeout par toast (sauf variant 'error')
   - Cleanup : tous les setTimeout sont clear au unmount du Provider
     pour éviter les leaks et les setState après unmount

   Détection mobile : on utilise Capacitor.isNativePlatform() PUIS un
   media query CSS comme fallback (positionnement géré côté CSS du
   container, pas côté JS — plus robuste). Le seul bit JS est le check
   isNative pour appliquer la safe-area-inset-top sur iOS.
   ============================================================ */
import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { isNative } from '../../utils/platform';
import Toast from './Toast';

// Durées par défaut par variant (ms). 0 = pas d'auto-dismiss (error).
const DEFAULT_DURATIONS = {
  success: 3000,
  error:   0,    // sticky : l'utilisateur DOIT voir l'erreur
  warning: 5000,
  info:    3000,
};

// Limite de la stack — au-delà, le plus ancien est éjecté (FIFO).
const MAX_STACK = 3;

export const ToastContext = createContext(null);

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  // Map { toastId → timeoutId } pour pouvoir clear individuellement
  // et purger entièrement au unmount.
  const timeoutsRef = useRef(new Map());

  // ID counter monotone (UUID inutile ici, suffit pour les keys React).
  const idCounterRef = useRef(0);

  // Détection mobile native UNE fois au mount (Capacitor.isNativePlatform()
  // est stable après initialisation de l'app — pas besoin de réévaluer).
  const isNativeRef = useRef(isNative());

  /**
   * Retire un toast par id et clear son timeout associé.
   * Utilisé par le bouton X et par l'auto-dismiss.
   */
  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const tid = timeoutsRef.current.get(id);
    if (tid) {
      clearTimeout(tid);
      timeoutsRef.current.delete(id);
    }
  }, []);

  /**
   * Ajoute un toast à la stack (FIFO max 3).
   * Si la stack est pleine, le plus ancien est dismiss.
   * Si la durée est > 0, on programme l'auto-dismiss.
   */
  const enqueue = useCallback((variant, message, options = {}) => {
    // Garde-fou : message vide ou non-string → ignore silencieusement
    if (!message || typeof message !== 'string') {
      console.warn('[Toast] message vide ou non-string ignoré');
      return;
    }

    const id = ++idCounterRef.current;
    const duration = options.duration !== undefined
      ? options.duration
      : DEFAULT_DURATIONS[variant] ?? 3000;

    setToasts(prev => {
      // Stack FIFO max 3 : si la stack est pleine, on retire le plus ancien
      // (et on clear son timeout pour ne pas qu'il dismiss un toast suivant).
      let next = prev;
      if (next.length >= MAX_STACK) {
        const oldest = next[0];
        next = next.slice(1);
        const oldTid = timeoutsRef.current.get(oldest.id);
        if (oldTid) {
          clearTimeout(oldTid);
          timeoutsRef.current.delete(oldest.id);
        }
      }
      return [...next, { id, variant, message, duration }];
    });

    // Auto-dismiss programmé uniquement si duration > 0 (error = sticky)
    if (duration > 0) {
      const tid = setTimeout(() => dismiss(id), duration);
      timeoutsRef.current.set(id, tid);
    }
  }, [dismiss]);

  // API exposée par le contexte — méthodes par variant
  const api = useRef({
    success: (message, options) => enqueue('success', message, options),
    error:   (message, options) => enqueue('error',   message, options),
    warning: (message, options) => enqueue('warning', message, options),
    info:    (message, options) => enqueue('info',    message, options),
  });
  // Re-binder enqueue dans api.current si la ref doit suivre.
  // (enqueue est stable car wrappé en useCallback avec [dismiss],
  // et dismiss est stable [], donc enqueue ne change jamais — on peut
  // se permettre de capturer une seule fois. Pas besoin de useEffect.)

  // Cleanup global : au unmount du Provider, clear TOUS les timeouts
  // pour éviter les setState après unmount (warning React) et les
  // leaks mémoire si l'app est démontée pendant qu'un toast est actif.
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach(tid => clearTimeout(tid));
      timeouts.clear();
    };
  }, []);

  // Style du container selon la plateforme.
  // Sur natif iOS : top + safe-area pour passer sous le notch.
  // Sur web mobile (<=768px) : top-center via media query CSS (cf. styles).
  // Sur desktop : bottom-right.
  const containerStyle = {
    position: 'fixed',
    zIndex: 10000, // au-dessus de tout (modales, overlays)
    pointerEvents: 'none', // les toasts eux-mêmes réactivent pointer-events
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    ...(isNativeRef.current
      ? {
          top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          left: '50%',
          transform: 'translateX(-50%)',
          alignItems: 'center',
        }
      : {
          // Desktop : bottom-right. Sur mobile web, la media query CSS
          // ci-dessous override en top-center.
          bottom: '20px',
          right: '20px',
          alignItems: 'flex-end',
        }),
  };

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div
        className="revyo-toast-container"
        style={containerStyle}
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map(t => (
          <Toast
            key={t.id}
            variant={t.variant}
            message={t.message}
            onDismiss={() => dismiss(t.id)}
          />
        ))}
      </div>

      {/*
        Override mobile web (non-Capacitor) : si on est sur un browser
        mobile <=768px, on passe en top-center. Sur Capacitor natif,
        le inline style ci-dessus a déjà appliqué top + center.
        On utilise un <style> inline pour ne pas créer un CSS module
        supplémentaire pour ce seul override.
      */}
      {!isNativeRef.current && (
        <style>{`
          @media (max-width: 768px) {
            .revyo-toast-container {
              top: 12px !important;
              bottom: auto !important;
              left: 50% !important;
              right: auto !important;
              transform: translateX(-50%) !important;
              align-items: center !important;
            }
          }
        `}</style>
      )}
    </ToastContext.Provider>
  );
}
